/** Reopening a saved basket.
 *
 * Loading moves the SESSION and the EXPIRY too, not just the legs. A
 * basket priced against a different day would be meaningless, and quietly
 * keeping the current date is exactly the wrong-but-plausible state this
 * app has been bitten by before — it looks like it worked.
 *
 * Auto-run stops as well: arriving mid-playback on a session you did not
 * choose is disorienting, and the clock would be somewhere unrelated to
 * the basket you just opened.
 */

import { useCallback } from "react";
import type { SimLeg, SimSaved } from "@/lib/simulatorApi";

interface Deps {
  pickIndex: (underlying: string) => void;
  pickDate: (tradeDate: string) => void;
  setExpiry: (expiry: string) => void;
  replaceAll: (legs: SimLeg[]) => void;
  stopAutoRun: () => void;
}

export function useLoadSaved(deps: Deps): (saved: SimSaved) => void {
  const { pickIndex, pickDate, setExpiry, replaceAll, stopAutoRun } = deps;
  return useCallback(
    (saved: SimSaved) => {
      stopAutoRun();
      pickIndex(saved.underlying);
      pickDate(saved.trade_date);
      setExpiry(saved.expiry);
      replaceAll(
        saved.legs.map((leg) => ({
          strike: leg.strike,
          side: leg.side,
          direction: leg.direction,
          lots: leg.lots,
          entry: leg.entry,
          // Dropped rather than sent as null: the API treats a missing
          // entry minute as "held all session", which is what a leg saved
          // without one meant.
          ...(leg.entry_at !== null ? { entry_at: leg.entry_at } : {}),
        })),
      );
    },
    [pickIndex, pickDate, setExpiry, replaceAll, stopAutoRun],
  );
}
